import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

// Your Google Client ID — set REACT_APP_GOOGLE_CLIENT_ID in .env
// (Create at console.cloud.google.com → APIs & Services → Credentials → OAuth 2.0 Client ID)
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

export default function GoogleAuthButton({ redirectTo = "/app", inviteToken = "", ageConfirmed = false, onError }) {
  const { loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  // Load the Google Identity Services script once
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    if (document.getElementById("google-gsi-script")) {
      setScriptLoaded(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "google-gsi-script";
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Initialize Google and render the button once the script is ready
  useEffect(() => {
    if (!scriptLoaded || !GOOGLE_CLIENT_ID || !buttonRef.current) return;

    window.google?.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: true,
    });

    window.google?.accounts.id.renderButton(buttonRef.current, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "continue_with",
      shape: "rectangular",
      logo_alignment: "left",
      width: buttonRef.current.offsetWidth || 340,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded]);

  async function handleCredentialResponse(response) {
    if (!response?.credential) return;
    setBusy(true);
    try {
      const result = await loginWithGoogle(response.credential, { age_confirmed: ageConfirmed });
      if (inviteToken) {
        try {
          const { familyApi } = await import("../../services/api");
          await familyApi.acceptInvite(inviteToken);
        } catch {
          // non-fatal — proceed to app
        }
      }
      navigate(inviteToken ? redirectTo : (result?.is_new_user ? "/welcome" : redirectTo), { replace: true });
    } catch (err) {
      onError?.(err.message || "Google sign-in failed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (!GOOGLE_CLIENT_ID) {
    return (
      <div className="hm-auth-google-missing">
        Google sign-in not configured.
      </div>
    );
  }

  return (
    <div className="hm-auth-google-wrap">
      {busy && (
        <div className="hm-auth-google-busy">
          <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
          Signing in with Google…
        </div>
      )}
      <div
        ref={buttonRef}
        className="hm-auth-google-btn"
        style={{ opacity: busy ? 0.5 : 1, pointerEvents: busy ? "none" : "auto" }}
      />
    </div>
  );
}
